import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { supabase } from './supabase';
import type { IntercambioMessage, IntercambioThread } from '../types/types';

// Pre-filled inquiry text used by the Intercambio contact buttons (in-app
// message + WhatsApp). Keeps the wording consistent across PropertyCard,
// list view, and detail view.
// The link is the CRM detail route, not the portal URL: the receiver is the
// property OWNER, so /propiedades/:id always resolves for them once logged in,
// while a shared property is not necessarily published to the portal.
export function buildIntercambioInquiry(
  title: string | null | undefined,
  iiId: string | null | undefined,
  propertyId?: string | null,
): string {
  const cleanTitle = (title ?? '').trim() || 'la propiedad';
  const head = iiId
    ? `Hola, me interesa la propiedad "${cleanTitle}" (#${iiId}).`
    : `Hola, me interesa la propiedad "${cleanTitle}".`;
  return propertyId ? `${head}\n\n${crmOrigin()}/propiedades/${propertyId}` : head;
}

// ponytail: origin from the browser so dev/staging links stay on their own
// host; the literal is only for non-browser callers (tests).
function crmOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : 'https://intercambioinmobiliario.com';
}

// Same idea as buildIntercambioInquiry but for shared requirements: the
// consuming agent has a property that might match, so the opener flips —
// instead of "me interesa" it announces a possible match.
export function buildIntercambioRequirementInquiry(name: string | null | undefined): string {
  const cleanName = (name ?? '').trim() || 'el requerimiento';
  return `Hola, vi tu requerimiento "${cleanName}" en Intercambio. Podríamos tener algo que coincida.`;
}

// UA-based mobile check. The Call (tel:) link is only useful on devices that
// can place phone calls; on desktop it's noise, so the button is hidden.
export function isMobileDeviceUA(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /android|iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function useIntercambioThreads() {
  const [threads, setThreads] = useState<IntercambioThread[]>([]);
  const [loading, setLoading] = useState(true);
  // Per-mount unique channel name (mirrors mobile intercambio-message-hooks).
  // A static name breaks whenever TWO mounts coexist: supabase.channel(name)
  // returns the already-subscribed instance and the second .on() throws
  // "cannot add postgres_changes callbacks after subscribe()" — which happened
  // on EVERY visit to Intercambio→Mensajes, since Sidebar also uses this hook
  // (GlitchTip intver2-web #975, 61 events).
  const channelId = useId();

  const refresh = useCallback(async () => {
    const { data, error } = await supabase.rpc('list_intercambio_threads');
    if (!error && Array.isArray(data)) setThreads(data as IntercambioThread[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Realtime: refresh on any change to intercambio_messages for this user.
  // Server-side RLS already restricts what the subscription emits.
  useEffect(() => {
    const channel = supabase
      .channel(`intercambio_messages_threads_${channelId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'intercambio_messages' },
        () => { refresh(); },
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [refresh, channelId]);

  const totalUnread = threads.reduce((sum, t) => sum + (t.unread_count || 0), 0);

  return { threads, loading, refresh, totalUnread };
}

interface ThreadKey {
  peerProfileId: string;
  propertyId?: string | null;
  requirementId?: string | null;
}

export function useIntercambioThreadMessages(key: ThreadKey | null) {
  const [messages, setMessages] = useState<IntercambioMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const latestKey = useRef<ThreadKey | null>(null);
  // Same collision class as useIntercambioThreads: a key-derived name repeats
  // when the same thread remounts before the previous cleanup lands.
  const channelId = useId();

  const refresh = useCallback(async () => {
    if (!key) return;
    latestKey.current = key;
    setLoading(true);
    const { data, error } = await supabase.rpc('list_intercambio_thread_messages', {
      p_peer_profile_id: key.peerProfileId,
      p_property_id: key.propertyId ?? null,
      p_requirement_id: key.requirementId ?? null,
    });
    // Guard against race: if the key changed while we were awaiting, discard.
    if (latestKey.current === key && !error && Array.isArray(data)) {
      setMessages(data as IntercambioMessage[]);
    }
    setLoading(false);
  }, [key]);

  useEffect(() => {
    if (!key) { setMessages([]); return; }
    refresh();
  }, [key, refresh]);

  // Realtime subscription scoped to messages involving this peer.
  useEffect(() => {
    if (!key) return;
    const channel = supabase
      .channel(`intercambio_messages_thread_${key.peerProfileId}_${key.propertyId ?? 'na'}_${key.requirementId ?? 'na'}_${channelId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'intercambio_messages' },
        (payload) => {
          const row = payload.new as IntercambioMessage;
          const peer = key.peerProfileId;
          const matchesPeer = row.sender_id === peer || row.receiver_id === peer;
          const matchesProperty = (row.property_id ?? null) === (key.propertyId ?? null);
          const matchesRequirement = (row.requirement_id ?? null) === (key.requirementId ?? null);
          if (matchesPeer && matchesProperty && matchesRequirement) {
            setMessages((prev) =>
              prev.some((m) => m.id === row.id) ? prev : [...prev, row],
            );
          }
        },
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [key, channelId]);

  return { messages, loading, refresh };
}

export async function sendIntercambioMessage(input: {
  receiverId: string;
  message: string;
  subject?: string | null;
  propertyId?: string | null;
  requirementId?: string | null;
}): Promise<{ ok: true; row: IntercambioMessage } | { ok: false; error: string }> {
  const { data, error } = await supabase.rpc('send_intercambio_message', {
    p_receiver_id: input.receiverId,
    p_message: input.message,
    p_subject: input.subject ?? null,
    p_property_id: input.propertyId ?? null,
    p_requirement_id: input.requirementId ?? null,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true, row: data as IntercambioMessage };
}

export async function markIntercambioThreadRead(key: ThreadKey): Promise<number> {
  const { data, error } = await supabase.rpc('mark_intercambio_thread_read', {
    p_peer_profile_id: key.peerProfileId,
    p_property_id: key.propertyId ?? null,
    p_requirement_id: key.requirementId ?? null,
  });
  if (error) return 0;
  return Number(data ?? 0);
}

// Fetch the minimum peer/property/requirement context for a brand-new thread
// that doesn't exist yet on the server. Used when the inbox opens with URL
// params but no matching thread has been created.
export async function resolveNewThreadContext(input: {
  peerProfileId: string;
  propertyId?: string | null;
  requirementId?: string | null;
}): Promise<IntercambioThread | null> {
  // Peer profile: must use the SECURITY DEFINER `get_colleague_detail` RPC
  // because the `profiles` table RLS only exposes own-agency rows (or
  // super_admin), so a direct `.from('profiles').select(...)` returns null
  // for any cross-agency colleague — and Intercambio is by definition
  // cross-agency. Without this, the deep-linked thread would never resolve
  // and the messages page would land on the empty state.
  // Property + requirement rows: also agency-scoped via RLS, so direct
  // SELECTs fail for cross-agency. We let them fail silently — the thread
  // still works for messaging; the header just falls back to generic
  // "Propiedad" / "Requerimiento" labels until first reply populates the
  // real thread row from the messages RPC.
  const [colleagueRpc, propertyRow, requirementRow] = await Promise.all([
    supabase.rpc('get_colleague_detail', { p_profile_id: input.peerProfileId }),
    input.propertyId
      ? supabase
          .from('properties')
          .select('id, ii_id, title')
          .eq('id', input.propertyId)
          .maybeSingle()
      : Promise.resolve({ data: null }),
    input.requirementId
      ? supabase
          .from('requirements')
          .select('id, name')
          .eq('id', input.requirementId)
          .maybeSingle()
      : Promise.resolve({ data: null }),
  ]);
  const detail = (colleagueRpc.data as any) ?? null;
  const peerProfile = detail?.profile ?? null;
  if (!peerProfile?.id) return null;
  return {
    peer_profile_id: peerProfile.id,
    peer_full_name: peerProfile.full_name ?? null,
    peer_photo_url: peerProfile.photo_url ?? null,
    peer_agency_name: detail?.agency?.name ?? null,
    property_id: input.propertyId ?? null,
    property_ii_id: (propertyRow.data as any)?.ii_id ?? null,
    property_title: (propertyRow.data as any)?.title ?? null,
    requirement_id: input.requirementId ?? null,
    requirement_name: (requirementRow.data as any)?.name ?? null,
    last_message: '',
    last_message_at: new Date().toISOString(),
    last_from_me: false,
    unread_count: 0,
  };
}
